import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { blogPostsData } from '../../data/blogPostsData';

const BlogPreview = () => {
  // Get the 6 most recent blog posts
  const recentPosts = blogPostsData.slice(0, 6);
  
  // State for image loading errors
  const [imgErrors, setImgErrors] = useState<{[key: string]: boolean}>({});
  
  const handleImageError = (postId: string) => {
    setImgErrors(prev => ({
      ...prev,
      [postId]: true
    }));
  };

  return (
    <section id="blog-preview" className="py-24 bg-gradient-to-b from-gray-50 to-white dark:from-dark-900 dark:to-dark-800">
      <div className="container mx-auto px-4">
        {/* SEO-optimized heading and description */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Best Text to Speech Guides & AI Voice Insights
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Expert reviews of the best text to speech software 2025, TTS evolution insights, free AI voice generator guides, and tips for content creators.
          </p>
        </div>
        
        {/* Blog cards grid - 3 cards per row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {recentPosts.map(post => (
            <div key={post.id} className="bg-white dark:bg-dark-800 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 transition-transform hover:-translate-y-1 hover:shadow-md">
              <Link to={`/blog/${post.slug}`} className="block">
                <div className={`relative w-full h-48 bg-gradient-to-r from-purple-600 to-blue-600 overflow-hidden`}>
                  {!imgErrors[post.id] ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(post.id)}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-xl p-4 text-center">
                      {post.title}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                    <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-1 rounded-full">
                      {post.focusKeyword}
                    </span>
                    <span className="mx-2">•</span>
                    <span>{post.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-purple-600 dark:text-purple-400 font-medium">
                    Read More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
        
        {/* View all blogs CTA */}
        <div className="text-center mt-12">
          <Link 
            to="/blog" 
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            onClick={() => {
              // Navigate to blog page and ensure it scrolls to top
              setTimeout(() => {
                window.scrollTo(0, 0);
                document.body.scrollTop = 0; // For Safari
                document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
              }, 100);
            }}
          >
            View All Articles
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
